/*
 * Copia del preset `NfarmPreset` di @arpes-is/angular-20-ui@1.0.0-alpha.8 (tema nfarm del QdC).
 * Da sostituire con la libreria quando avrà una versione compatibile con Angular 21.
 */
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Preset PrimeNG dell'applicazione, allineato ai token dell'e-commerce nfarm
 * (verde Novatellus, superfici calde, raggi morbidi).
 *
 * Gli schemi sono due, come in PrimeNG: chiaro e scuro (`.app-dark`). Il terzo tema,
 * "darkest", è lo schema scuro con superfici più profonde: lo applica
 * `styles/nfarm-theme.css` con la classe `.app-darkest`.
 *
 * Contrasti (WCAG 2.1 AA, EN 301 549 §9):
 * - primario chiaro #46806A con testo bianco: 4,61:1;
 * - primario scuro #8CC5A8 con testo #10201A: 9,1:1;
 * - bordi dei campi ≥ 3:1 sul fondo (WCAG 1.4.11), testo "muted" ≥ 4,5:1;
 * - i pulsanti di severità usano tonalità scure, perché i 500 di Aura con testo
 *   bianco non arrivano a 4,5:1.
 */
export const NfarmPreset = definePreset(Aura, {
    primitive: {
        borderRadius: {
            none: '0',
            xs: '4px',
            sm: '6px',
            md: '10px',
            lg: '12px',
            xl: '16px',
        },
    },
    semantic: {
        primary: {
            50: '#f3f8f5',
            100: '#eaf1ed',
            200: '#cbddd3',
            300: '#a8c6b6',
            400: '#78a892',
            500: '#46806a',
            600: '#3b6e5a',
            700: '#2e5747',
            800: '#22352e',
            900: '#16241e',
            950: '#0e1813',
        },
        focusRing: {
            width: '2px',
            style: 'solid',
            color: '{primary.color}',
            offset: '2px',
            shadow: 'none',
        },
        formField: {
            paddingX: '0.8125rem',
            paddingY: '0.5625rem',
            borderRadius: '10px',
            focusRing: {
                width: '0',
                style: 'none',
                color: 'transparent',
                offset: '0',
                shadow: '0 0 0 3px color-mix(in srgb, {primary.color} 24%, transparent)',
            },
            transitionDuration: '0.15s',
        },
        content: {
            borderRadius: '16px',
        },
        overlay: {
            select: { borderRadius: '12px', shadow: '0 14px 34px rgba(20, 32, 27, 0.16)' },
            popover: { borderRadius: '14px', padding: '0.875rem', shadow: '0 14px 34px rgba(20, 32, 27, 0.16)' },
            modal: { borderRadius: '20px', padding: '1.5rem', shadow: '0 32px 80px rgba(20, 32, 27, 0.34)' },
            navigation: { shadow: '0 14px 34px rgba(20, 32, 27, 0.16)' },
        },
        list: {
            padding: '0.375rem',
            gap: '2px',
            option: { padding: '0.5625rem 0.75rem', borderRadius: '8px' },
            optionGroup: { padding: '0.5625rem 0.75rem', fontWeight: '700' },
        },
        navigation: {
            item: { padding: '0.5625rem 0.75rem', borderRadius: '8px', gap: '0.625rem' },
        },
        colorScheme: {
            light: {
                surface: {
                    0: '#ffffff',
                    50: '#fbfaf8',
                    100: '#f5f3f0',
                    200: '#ede9e2',
                    300: '#e3dfd7',
                    400: '#c6c2b9',
                    500: '#a8a49a',
                    600: '#8a8679',
                    700: '#6a716b',
                    800: '#46504a',
                    900: '#22352e',
                    950: '#16241e',
                },
                primary: {
                    color: '{primary.500}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.700}',
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '#3b6e5a',
                    focusColor: '{primary.700}',
                },
                mask: {
                    background: 'rgba(20, 32, 27, 0.52)',
                    color: '{surface.200}',
                },
                formField: {
                    background: '#ffffff',
                    disabledBackground: '{surface.100}',
                    filledBackground: '{surface.50}',
                    filledHoverBackground: '{surface.50}',
                    filledFocusBackground: '#ffffff',
                    borderColor: '#a39a86',
                    hoverBorderColor: '{primary.400}',
                    focusBorderColor: '{primary.500}',
                    invalidBorderColor: '#a0574f',
                    color: '{surface.900}',
                    disabledColor: '{surface.600}',
                    placeholderColor: '{surface.700}',
                    invalidPlaceholderColor: '#a0574f',
                    floatLabelColor: '{surface.700}',
                    floatLabelFocusColor: '{primary.600}',
                    floatLabelActiveColor: '{surface.700}',
                    floatLabelInvalidColor: '#a0574f',
                    iconColor: '{surface.700}',
                    shadow: 'none',
                },
                text: {
                    color: '{surface.900}',
                    hoverColor: '{surface.950}',
                    mutedColor: '{surface.700}',
                    hoverMutedColor: '{surface.800}',
                },
                content: {
                    background: '{surface.50}',
                    hoverBackground: '#efece6',
                    borderColor: '{surface.200}',
                    color: '{text.color}',
                    hoverColor: '{text.hover.color}',
                },
                overlay: {
                    select: { background: '#ffffff', borderColor: '{surface.200}', color: '{text.color}' },
                    popover: { background: '#ffffff', borderColor: '{surface.200}', color: '{text.color}' },
                    modal: { background: '{surface.50}', borderColor: '{surface.200}', color: '{text.color}' },
                },
                list: {
                    option: {
                        focusBackground: '{surface.100}',
                        selectedBackground: '{highlight.background}',
                        selectedFocusBackground: '{highlight.focus.background}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        selectedColor: '{highlight.color}',
                        selectedFocusColor: '{highlight.focus.color}',
                        icon: { color: '{surface.600}', focusColor: '{surface.700}' },
                    },
                    optionGroup: { background: 'transparent', color: '{text.muted.color}' },
                },
                navigation: {
                    item: {
                        focusBackground: '{surface.100}',
                        activeBackground: '{highlight.background}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        activeColor: '{highlight.color}',
                        icon: { color: '{surface.600}', focusColor: '{surface.700}', activeColor: '{highlight.color}' },
                    },
                },
            },
            dark: {
                surface: {
                    0: '#ffffff',
                    50: '#eef2ef',
                    100: '#d5ddd8',
                    200: '#b3c0b8',
                    300: '#8e9d95',
                    400: '#76857d',
                    500: '#5a6861',
                    600: '#46534c',
                    700: '#35413b',
                    800: '#28322d',
                    900: '#1d2622',
                    950: '#151c19',
                },
                primary: {
                    color: '#8cc5a8',
                    contrastColor: '#10201a',
                    hoverColor: '#a3d3ba',
                    activeColor: '#b8dfca',
                },
                highlight: {
                    background: 'rgba(140, 197, 168, 0.16)',
                    focusBackground: 'rgba(140, 197, 168, 0.24)',
                    color: '#b8e0a8',
                    focusColor: '#cdebc1',
                },
                mask: {
                    background: 'rgba(4, 8, 6, 0.64)',
                    color: '{surface.200}',
                },
                formField: {
                    background: '{surface.950}',
                    disabledBackground: '{surface.800}',
                    filledBackground: '{surface.800}',
                    filledHoverBackground: '{surface.800}',
                    filledFocusBackground: '{surface.950}',
                    borderColor: '{surface.500}',
                    hoverBorderColor: '{surface.400}',
                    focusBorderColor: '{primary.color}',
                    invalidBorderColor: '#e8a79f',
                    color: '{surface.0}',
                    disabledColor: '{surface.400}',
                    placeholderColor: '{surface.300}',
                    invalidPlaceholderColor: '#e8a79f',
                    floatLabelColor: '{surface.300}',
                    floatLabelFocusColor: '{primary.color}',
                    floatLabelActiveColor: '{surface.300}',
                    floatLabelInvalidColor: '#e8a79f',
                    iconColor: '{surface.300}',
                    shadow: 'none',
                },
                text: {
                    color: '{surface.50}',
                    hoverColor: '{surface.0}',
                    mutedColor: '{surface.300}',
                    hoverMutedColor: '{surface.200}',
                },
                content: {
                    background: '{surface.900}',
                    hoverBackground: '{surface.800}',
                    borderColor: '{surface.700}',
                    color: '{text.color}',
                    hoverColor: '{text.hover.color}',
                },
                overlay: {
                    select: { background: '{surface.900}', borderColor: '{surface.700}', color: '{text.color}' },
                    popover: { background: '{surface.900}', borderColor: '{surface.700}', color: '{text.color}' },
                    modal: { background: '{surface.900}', borderColor: '{surface.700}', color: '{text.color}' },
                },
                list: {
                    option: {
                        focusBackground: '{surface.800}',
                        selectedBackground: '{highlight.background}',
                        selectedFocusBackground: '{highlight.focus.background}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        selectedColor: '{highlight.color}',
                        selectedFocusColor: '{highlight.focus.color}',
                        icon: { color: '{surface.400}', focusColor: '{surface.300}' },
                    },
                    optionGroup: { background: 'transparent', color: '{text.muted.color}' },
                },
                navigation: {
                    item: {
                        focusBackground: '{surface.800}',
                        activeBackground: '{highlight.background}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        activeColor: '{highlight.color}',
                        icon: { color: '{surface.400}', focusColor: '{surface.300}', activeColor: '{highlight.color}' },
                    },
                },
            },
        },
    },
    components: {
        button: {
            root: {
                borderRadius: '10px',
                paddingX: '1.125rem',
                paddingY: '0.5625rem',
                label: { fontWeight: '600' },
                sm: { fontSize: '0.8125rem', paddingX: '0.75rem', paddingY: '0.375rem' },
                lg: { fontSize: '1rem', paddingX: '1.375rem', paddingY: '0.75rem' },
            },
            colorScheme: {
                light: {
                    root: {
                        secondary: {
                            background: '{surface.100}',
                            hoverBackground: '{surface.200}',
                            activeBackground: '{surface.300}',
                            borderColor: '{surface.200}',
                            hoverBorderColor: '{surface.300}',
                            activeBorderColor: '{surface.400}',
                            color: '{surface.800}',
                            hoverColor: '{surface.900}',
                            activeColor: '{surface.950}',
                            focusRing: { color: '{surface.700}', shadow: 'none' },
                        },
                        info: {
                            background: '{sky.700}',
                            hoverBackground: '{sky.800}',
                            activeBackground: '{sky.900}',
                            borderColor: '{sky.700}',
                            hoverBorderColor: '{sky.800}',
                            activeBorderColor: '{sky.900}',
                            focusRing: { color: '{sky.700}', shadow: 'none' },
                        },
                        success: {
                            background: '{primary.500}',
                            hoverBackground: '{primary.600}',
                            activeBackground: '{primary.700}',
                            borderColor: '{primary.500}',
                            hoverBorderColor: '{primary.600}',
                            activeBorderColor: '{primary.700}',
                            focusRing: { color: '{primary.500}', shadow: 'none' },
                        },
                        warn: {
                            background: '#b0752f',
                            hoverBackground: '#96622a',
                            activeBackground: '#7d5122',
                            borderColor: '#b0752f',
                            hoverBorderColor: '#96622a',
                            activeBorderColor: '#7d5122',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: { color: '#b0752f', shadow: 'none' },
                        },
                        help: {
                            background: '{purple.600}',
                            hoverBackground: '{purple.700}',
                            activeBackground: '{purple.800}',
                            borderColor: '{purple.600}',
                            hoverBorderColor: '{purple.700}',
                            activeBorderColor: '{purple.800}',
                            focusRing: { color: '{purple.600}', shadow: 'none' },
                        },
                        danger: {
                            background: '#a0574f',
                            hoverBackground: '#8e4a42',
                            activeBackground: '#6f3a34',
                            borderColor: '#a0574f',
                            hoverBorderColor: '#8e4a42',
                            activeBorderColor: '#6f3a34',
                            focusRing: { color: '#a0574f', shadow: 'none' },
                        },
                        contrast: {
                            background: '{primary.800}',
                            hoverBackground: '{primary.900}',
                            activeBackground: '{primary.950}',
                            borderColor: '{primary.800}',
                            hoverBorderColor: '{primary.900}',
                            activeBorderColor: '{primary.950}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: { color: '{primary.800}', shadow: 'none' },
                        },
                    },
                    outlined: {
                        primary: { hoverBackground: '{primary.50}', activeBackground: '{primary.100}', borderColor: '{primary.400}', color: '#3b6e5a' },
                        secondary: { hoverBackground: '{surface.100}', activeBackground: '{surface.200}', borderColor: '{surface.400}', color: '{surface.800}' },
                        success: { borderColor: '{primary.400}', color: '#3b6e5a' },
                        info: { color: '{sky.700}', borderColor: '{sky.600}' },
                        warn: { color: '#96622a', borderColor: '#c98a3f' },
                        help: { color: '{purple.700}', borderColor: '{purple.400}' },
                        danger: { hoverBackground: '#f9eeec', activeBackground: '#f6e3e0', color: '#8e4a42', borderColor: '#c98f87' },
                    },
                    text: {
                        primary: { hoverBackground: '{primary.50}', activeBackground: '{primary.100}', color: '#3b6e5a' },
                        secondary: { hoverBackground: '{surface.100}', activeBackground: '{surface.200}', color: '{surface.800}' },
                        success: { color: '#3b6e5a' },
                        info: { color: '{sky.700}' },
                        warn: { color: '#96622a' },
                        help: { color: '{purple.700}' },
                        danger: { hoverBackground: '#f9eeec', activeBackground: '#f6e3e0', color: '#8e4a42' },
                    },
                },
                dark: {
                    root: {
                        secondary: {
                            background: '{surface.800}',
                            hoverBackground: '{surface.700}',
                            activeBackground: '{surface.600}',
                            borderColor: '{surface.700}',
                            hoverBorderColor: '{surface.600}',
                            activeBorderColor: '{surface.500}',
                            color: '{surface.50}',
                            hoverColor: '{surface.0}',
                            activeColor: '{surface.0}',
                            focusRing: { color: '{surface.300}', shadow: 'none' },
                        },
                        success: {
                            background: '{primary.color}',
                            hoverBackground: '{primary.hover.color}',
                            activeBackground: '{primary.active.color}',
                            borderColor: '{primary.color}',
                            hoverBorderColor: '{primary.hover.color}',
                            activeBorderColor: '{primary.active.color}',
                            color: '{primary.contrast.color}',
                            hoverColor: '{primary.contrast.color}',
                            activeColor: '{primary.contrast.color}',
                            focusRing: { color: '{primary.color}', shadow: 'none' },
                        },
                        danger: {
                            background: '#e8a79f',
                            hoverBackground: '#efbdb6',
                            activeBackground: '#f4cfca',
                            borderColor: '#e8a79f',
                            hoverBorderColor: '#efbdb6',
                            activeBorderColor: '#f4cfca',
                            color: '#3a1714',
                            hoverColor: '#3a1714',
                            activeColor: '#3a1714',
                            focusRing: { color: '#e8a79f', shadow: 'none' },
                        },
                        contrast: {
                            background: '{surface.0}',
                            hoverBackground: '{surface.100}',
                            activeBackground: '{surface.200}',
                            borderColor: '{surface.0}',
                            hoverBorderColor: '{surface.100}',
                            activeBorderColor: '{surface.200}',
                            color: '{surface.950}',
                            hoverColor: '{surface.950}',
                            activeColor: '{surface.950}',
                            focusRing: { color: '{surface.0}', shadow: 'none' },
                        },
                    },
                    outlined: {
                        primary: { hoverBackground: 'rgba(140, 197, 168, 0.08)', activeBackground: 'rgba(140, 197, 168, 0.16)', borderColor: '#5e9a7e', color: '{primary.color}' },
                        secondary: { hoverBackground: 'rgba(255, 255, 255, 0.05)', activeBackground: 'rgba(255, 255, 255, 0.1)', borderColor: '{surface.500}', color: '{surface.100}' },
                        danger: { hoverBackground: 'rgba(232, 167, 159, 0.08)', activeBackground: 'rgba(232, 167, 159, 0.16)', borderColor: '#b9776f', color: '#e8a79f' },
                    },
                    text: {
                        primary: { hoverBackground: 'rgba(140, 197, 168, 0.08)', activeBackground: 'rgba(140, 197, 168, 0.16)', color: '{primary.color}' },
                        secondary: { hoverBackground: 'rgba(255, 255, 255, 0.05)', activeBackground: 'rgba(255, 255, 255, 0.1)', color: '{surface.100}' },
                        danger: { hoverBackground: 'rgba(232, 167, 159, 0.08)', activeBackground: 'rgba(232, 167, 159, 0.16)', color: '#e8a79f' },
                    },
                },
            },
        },
        card: {
            root: { borderRadius: '16px', shadow: 'var(--ui-shadow-card)' },
            body: { padding: '1.5rem', gap: '0.75rem' },
            title: { fontSize: '1.125rem', fontWeight: '700' },
        },
        datatable: {
            columnTitle: { fontWeight: '600' },
            colorScheme: {
                light: {
                    headerCell: { background: 'transparent', color: '{text.muted.color}', borderColor: '{surface.200}' },
                    row: { background: 'transparent', hoverBackground: '{surface.100}', stripedBackground: '{surface.100}' },
                    bodyCell: { borderColor: '{surface.200}' },
                    header: { background: 'transparent' },
                    footer: { background: 'transparent' },
                },
                dark: {
                    headerCell: { background: 'transparent', color: '{text.muted.color}', borderColor: '{surface.700}' },
                    row: { background: 'transparent', hoverBackground: '{surface.800}', stripedBackground: '{surface.800}' },
                    bodyCell: { borderColor: '{surface.700}' },
                    header: { background: 'transparent' },
                    footer: { background: 'transparent' },
                },
            },
        },
        paginator: {
            root: { padding: '0.75rem 0', background: 'transparent' },
            navButton: { borderRadius: '8px' },
        },
        dialog: {
            header: { padding: '1.5rem 1.625rem 0.5rem' },
            title: { fontSize: '1.1875rem', fontWeight: '700' },
            content: { padding: '0.75rem 1.625rem 1.5rem' },
            footer: { padding: '1rem 1.625rem 1.5rem', gap: '0.625rem' },
        },
        tag: {
            root: { fontSize: '0.75rem', fontWeight: '600', padding: '0.25rem 0.625rem', borderRadius: '999px' },
            colorScheme: {
                light: {
                    primary: { background: '{primary.100}', color: '{primary.700}' },
                    secondary: { background: '{surface.200}', color: '{surface.800}' },
                    success: { background: '{primary.100}', color: '{primary.700}' },
                },
                dark: {
                    primary: { background: 'rgba(140, 197, 168, 0.16)', color: '{primary.color}' },
                    secondary: { background: '{surface.700}', color: '{surface.100}' },
                    success: { background: 'rgba(140, 197, 168, 0.16)', color: '{primary.color}' },
                },
            },
        },
        tooltip: {
            root: { borderRadius: '8px', padding: '0.4375rem 0.625rem', maxWidth: '18rem' },
            colorScheme: {
                light: { root: { background: '{primary.800}', color: '#ffffff' } },
                dark: { root: { background: '{surface.50}', color: '{surface.950}' } },
            },
        },
        tabs: {
            tab: { padding: '0.75rem 1rem', fontWeight: '600' },
            activeBar: { height: '2px' },
        },
        togglebutton: {
            root: { borderRadius: '10px', padding: '0.1875rem', fontWeight: '600' },
            content: { borderRadius: '8px', padding: '0.3125rem 0.75rem', checkedShadow: '0 1px 3px rgba(34, 53, 46, 0.16)' },
            colorScheme: {
                light: {
                    root: {
                        background: '{surface.200}',
                        checkedBackground: '{surface.200}',
                        hoverBackground: '{surface.200}',
                        borderColor: '{surface.200}',
                        checkedBorderColor: '{surface.200}',
                        color: '{surface.700}',
                        hoverColor: '{surface.900}',
                        checkedColor: '{surface.900}',
                    },
                    content: { checkedBackground: '#ffffff' },
                    icon: { color: '{surface.700}', hoverColor: '{surface.900}', checkedColor: '{primary.600}' },
                },
                dark: {
                    root: {
                        background: '{surface.950}',
                        checkedBackground: '{surface.950}',
                        hoverBackground: '{surface.950}',
                        borderColor: '{surface.950}',
                        checkedBorderColor: '{surface.950}',
                        color: '{surface.300}',
                        hoverColor: '{surface.0}',
                        checkedColor: '{surface.0}',
                    },
                    content: { checkedBackground: '{surface.700}' },
                    icon: { color: '{surface.300}', hoverColor: '{surface.0}', checkedColor: '{primary.color}' },
                },
            },
        },
    },
});

export default NfarmPreset;
